import { deepCloneWith, isRecord, value } from '@takeshape/util';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { updateAuthentication } from "../util/authentication.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
const title = 'Recharge';
const name = 'recharge';
const spec = 'https://raw.githubusercontent.com/takeshape/openapi-specs/84e701d4d7125b573df5c25d7fd9f3f04203a9fd/specs/recharge/2021-11.yml';
const serviceProvider = {
    name,
    title,
    description: 'Recharge is the leading subscriptions payments platform designed for merchants to set up and manage dynamic recurring billing across web and mobile.',
    categories: ['ecommerce', 'subscriptions'],
    links: [
        { title: 'Homepage', url: 'https://rechargepayments.com/' },
        { title: 'Docs', url: '/docs/services/providers/recharge' }
    ],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('recharge'),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: title
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: name
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: title
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    token: {
                        title: 'API Token',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['token']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable name for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'A unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'Find this under Apps > API Tokens'
                    }
                },
                order: ['token']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            id: formData.id,
            title: formData.title,
            provider: name,
            serviceType: 'openapi',
            authenticationType: 'bearer',
            namespace: formData.namespace,
            options: {
                endpoint: 'https://api.rechargeapps.com',
                spec,
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION,
                headers: {
                    'X-Recharge-Version': '2021-11'
                }
            }
        };
        let { authentication } = formData;
        authentication &&= { ...authentication };
        return updateAuthentication(config, formData, {
            type: 'bearer',
            header: 'X-Recharge-Access-Token'
        });
    },
    isCompleteConfig: hasEveryKey([
        'authentication',
        'options.endpoint',
        'options.spec',
        'options.headers.X-Recharge-Version'
    ]),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        // Expected — 200 OK or 401 Unauthorized with invalid auth
        endpoint: endpointWithPath('/store')
    }),
    specTransform(doc) {
        // Convert integer ids to strings
        return deepCloneWith(doc, (value) => {
            if (isRecord(value) && value.type === 'integer') {
                const { format, ...rest } = value;
                if (typeof format === 'string' && /^int\d\d$/.exec(format)) {
                    return { ...rest, type: 'string' };
                }
            }
        });
    }
};
export default serviceProvider;
